// Shared readers for the eligibility-scan result Scholar Path persists to
// localStorage (`sp-scan-result`) and the lead-capture form (`vg_lead`).
//
// One source of truth for "how many criteria are met": only criteria the
// scan marked `strong` count. `likely` rows are within reach — one document
// away — and are encouraged, never counted toward the 3-of-8 bar. The
// criteria set is the eight researcher criteria in `criteriaCatalog.ts`;
// ids from older scan payloads are normalised on read.

import {
  CRITERIA_IDS,
  CRITERIA_REQUIRED,
  CRITERIA_TOTAL,
  canonicalCriterionId,
  confirmHintFor,
  criterionLabel,
} from "./criteriaCatalog";

export { CRITERIA_TOTAL, CRITERIA_REQUIRED, criterionLabel };

export interface ScanCriterion {
  id: string;
  name?: string;
  strength?: string;
  note?: string;
  /** For likely rows: what would make this count. */
  confirm?: string | null;
}

export interface ScanResult {
  display_name?: string | null;
  affiliation?: string | null;
  citations_all?: number | null;
  h_index?: number | null;
  pub_count?: number | null;
  criteria?: ScanCriterion[];
  met_count?: number;
  likely_count?: number;
  verdict?: string;
  criteria_total?: number;
  criteria_required?: number;
  /** Name at the top of the CV, when one was sent. */
  cv_name?: string | null;
  /** The CV and the Scholar profile name different people. */
  name_mismatch?: boolean;
}

export const SCAN_RESULT_KEY = "sp-scan-result";
export const LEAD_KEY = "vg_lead";

/** Fold legacy ids into the catalog's eight and drop anything else. */
function normalise(result: ScanResult): ScanResult {
  const seen = new Set<string>();
  const criteria: ScanCriterion[] = [];
  for (const c of result.criteria || []) {
    const id = canonicalCriterionId(c?.id);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    criteria.push({ ...c, id });
  }
  criteria.sort((a, b) => CRITERIA_IDS.indexOf(a.id) - CRITERIA_IDS.indexOf(b.id));
  return { ...result, criteria };
}

export function readScanResult(): ScanResult | null {
  try {
    const raw = localStorage.getItem(SCAN_RESULT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? normalise(parsed as ScanResult) : null;
  } catch {
    return null;
  }
}

export function writeScanResult(data: ScanResult): void {
  try {
    localStorage.setItem(SCAN_RESULT_KEY, JSON.stringify(data));
  } catch {
    /* private mode */
  }
}

/** Criteria the scan scores — always a subset of the eight. */
export function scoredCriteria(result: ScanResult | null): ScanCriterion[] {
  return (result?.criteria || []).filter((c) => c && canonicalCriterionId(c.id));
}

export function strongCriteria(result: ScanResult | null): ScanCriterion[] {
  return scoredCriteria(result).filter((c) => c.strength === "strong");
}

export function likelyCriteria(result: ScanResult | null): ScanCriterion[] {
  return scoredCriteria(result).filter((c) => c.strength === "likely");
}

/** Strong-only count. Never trusts the backend's `met_count`. */
export function strongCount(result: ScanResult | null): number {
  return strongCriteria(result).length;
}

export function likelyCount(result: ScanResult | null): number {
  return likelyCriteria(result).length;
}

/** Short factual note for a criterion row — strong or likely. Prefers the
 *  numbers we have over whatever the backend wrote. */
export function criterionNote(c: ScanCriterion, scan: ScanResult | null): string {
  const cites = scan?.citations_all;
  const h = scan?.h_index;
  const pubs = scan?.pub_count;
  if (c.id === "original_contributions" && cites != null) {
    return `${Number(cites).toLocaleString()} citations${h ? ` · h-index ${h}` : ""}`;
  }
  if (c.id === "authorship" && pubs != null) {
    return `${pubs} peer-reviewed publications`;
  }
  return (c.note || "").trim();
}

/** One sentence that would turn a likely row into a met one. */
export function criterionConfirm(c: ScanCriterion): string {
  return (c.confirm || "").trim() || confirmHintFor(c.id);
}

/** "3 of 8 criteria supported · +2 within reach" — the one line used on the
 *  Case Plan and lead notes. */
export function eligibilitySummary(scan: ScanResult | null): string | null {
  if (!scan) return null;
  const met = strongCount(scan);
  const likely = likelyCount(scan);
  const base = `${met} of ${CRITERIA_TOTAL} criteria supported so far`;
  return likely ? `${base} · +${likely} within reach` : base;
}

export interface StoredLead {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  submittedAt?: number;
}

export function readLead(): StoredLead | null {
  try {
    const raw = localStorage.getItem(LEAD_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as StoredLead) : null;
  } catch {
    return null;
  }
}

/** First name the user typed into the contact modal, or "" when absent. */
export function readLeadFirstName(): string {
  const lead = readLead();
  return (lead?.firstName || "").trim();
}
